import { Condition, formatTicker } from '@toggle/toggle';
import { useEffect, useState } from 'react';

import { postEntities } from '~/api/entities/entity-service';
import { getSnakeMeta } from '~/views/turbo-chart/utils/chart-utils/chart-utils';

export const useEnrichedConditions = <T extends Condition>(
  baseConditions: T[]
) => {
  const [conditions, setConditions] = useState(baseConditions);
  const [loading, setLoading] = useState(false);

  const entitiesTags = baseConditions.flatMap(condition => condition.entity);

  const fetchSnakeAndEntityForCondition = async (
    abortController: AbortController
  ) => {
    setLoading(true);
    const snakeMetas = await Promise.all(
      baseConditions.map(item => getSnakeMeta(item.snake))
    );
    const [entities] = await Promise.all([
      postEntities(
        {
          filter: 2,
          args: entitiesTags,
        },
        abortController.signal
      ).then(response => {
        if (response.error || !response.data) {
          return;
        }
        return response.data;
      }),
    ]);
    const check = baseConditions.map((condition, idx) => {
      const matchingEntity = entities?.find(
        entity => entity.tag === condition.entity
      );

      if (matchingEntity) {
        return {
          ...condition,
          entityName: matchingEntity.name,
          formattedTicker: formatTicker(matchingEntity),
          snakeName: snakeMetas[idx]
            ? snakeMetas[idx]?.name.english
            : condition.snake,
        };
      } else {
        return condition;
      }
    });
    setLoading(false);
    setConditions(check);
  };

  useEffect(() => {
    const abortController = new AbortController();

    fetchSnakeAndEntityForCondition(abortController);

    return () => {
      abortController.abort();
    };
  }, [entitiesTags.join(',')]);

  return { conditions, loading };
};
